import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Users, Plus, Clock, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }) };
const CATS = ["general", "events", "photography", "videography", "tech", "lifestyle", "business"];

export default function CommunityPage() {
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "general" });

  useEffect(() => { axios.get(`${API}/forums`).then(r => setForums(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const createForum = async () => {
    if (!form.title) { toast.error("Title is required."); return; }
    try {
      const res = await axios.post(`${API}/forums`, form);
      setForums(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ title: "", description: "", category: "general" });
      toast.success("Forum created.");
    } catch { toast.error("Failed to create forum."); }
  };

  return (
    <div data-testid="community-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Community</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">The <span className="gold-gradient-text">Inner Circle</span></motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base max-w-xl">Join discussions, share experiences, and connect with fellow enthusiasts. Your voice shapes our community.</motion.p>
          </motion.div>

          <div className="flex items-center justify-between mb-8">
            <h2 className="text-white font-body text-sm font-medium">Forums ({forums.length})</h2>
            <button data-testid="create-forum-btn" onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 bg-gold/15 text-gold px-5 py-2 text-xs font-body hover:bg-gold/25 transition-colors"><Plus size={14} /> New Forum</button>
          </div>

          {showCreate && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-6 mb-8 space-y-4">
              <h3 className="text-white font-body text-sm font-medium">Create a New Forum</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Title *</label>
                  <Input data-testid="forum-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Forum topic" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Category</label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">{CATS.map(c => <SelectItem key={c} value={c} className="text-white/80 text-sm capitalize">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Description</label>
                <Textarea data-testid="forum-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What's this forum about?" rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" />
              </div>
              <button data-testid="submit-forum" onClick={createForum} className="bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors">Create Forum</button>
            </motion.div>
          )}

          {loading ? <div className="text-gold animate-pulse text-sm font-body">Loading forums...</div> : (
            <motion.div initial="hidden" animate="visible" className="space-y-4">
              {forums.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <MessageSquare size={48} className="text-gold/30 mx-auto mb-4" />
                  <h3 className="text-white font-heading text-xl mb-2">Start the Conversation</h3>
                  <p className="text-white/40 font-body text-sm">Create the first forum and get the community going.</p>
                </div>
              ) : forums.map((f, i) => (
                <motion.div key={f.id} variants={fadeUp} custom={i}>
                  <Link to={`/community/${f.id}`} data-testid={`forum-${f.id}`} className="glass-card p-6 block group hover:border-gold/30 transition-all">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 text-[10px] bg-gold/10 text-gold font-body uppercase">{f.category}</span>
                        </div>
                        <h3 className="text-white font-heading text-lg group-hover:text-gold transition-colors mb-1">{f.title}</h3>
                        {f.description && <p className="text-white/40 font-body text-xs leading-relaxed">{f.description}</p>}
                        {f.latest_post && <p className="text-white/25 text-[10px] font-body mt-2">Latest: {f.latest_post.author_name} — {f.latest_post.content?.slice(0, 60)}...</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1 text-white/30 text-xs font-body"><MessageSquare size={12} /> {f.post_count || 0}</div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
